import type { NativeTarget } from '@studio/models/control/preset';
import type { Device } from '@studio/models/device';
import type { RemovePreset } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import type { Proxy } from '#~/agent/types.js';
import { nativeWritesPersist } from '#~/api/storage/index.js';
import {
  pbNativeTargetToJs,
  resetOwnedNativeTarget,
  serializeDevicePresetSave,
  usesNativeOwner
} from '#~/control/preset/native-owner.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';

/**
 * Removes the specified device preset.
 *
 * The removal is saved first. A failed save leaves the camera untouched:
 * native records store no pose, so an erased camera slot could never be
 * written back, and the stored record would still point at it.
 *
 * Only then is an owned native target reset, and only to tidy the camera: it
 * never fails the deletion. An encoder without native presets (a rollback) or
 * an offline camera never answers the reset. A slot left set is reused on
 * VISCA and costs one preset on an ONVIF camera, which is better than a card
 * that can never be deleted. Runs in the device's preset-save queue so a save
 * in flight cannot take the slot between this write and the reset.
 */
export const removePreset: RemovePreset = (deviceId, presetId, token) =>
  serializeDevicePresetSave(deviceId, async () => {
    try {
      const proxy = locateProxy(deviceId);

      if (!proxy) {
        throw new Error('DP-230');
      }

      const existing =
        proxy.state.control?.studio?.device?.[deviceId]?.control?.presets?.items?.[presetId];
      const target = pbNativeTargetToJs(existing?.nativeTarget);

      await proxy.mutate(
        {},
        {
          control: {
            studio: {
              device: {
                [deviceId]: {
                  control: {
                    presets: {
                      items: {
                        [presetId]: {}
                      }
                    }
                  }
                }
              }
            }
          }
        }
      );

      await proxy.save(token);

      if (target) {
        await releaseNativeTarget(proxy, deviceId, target);
      }
    } catch (cause) {
      if (cause instanceof StudioError) {
        throw cause;
      }
      throw new StudioError('Unable to remove preset.', { cause });
    }
  });

/**
 * Resets the camera preset a deleted record pointed at, when that is safe and
 * possible. Every other outcome leaves the camera preset in place and logs why.
 */
const releaseNativeTarget = async (
  proxy: Proxy,
  deviceId: Device['id'],
  target: NativeTarget
): Promise<void> => {
  const orphan = (why: string) =>
    logger.warning(`Preset removed; its camera preset was left in place (${why}).`);
  if (!usesNativeOwner(proxy, deviceId)) {
    orphan('this encoder does not manage camera presets');
    return;
  }
  // A legacy write leaves native-only records to the backend's preservation of
  // the stored row, so the record may survive this deletion: keep its slot.
  if (!nativeWritesPersist(proxy.id)) {
    orphan('the deletion was saved without native preset persistence');
    return;
  }
  if (stillMapped(proxy, deviceId, target)) {
    orphan('another preset still uses it');
    return;
  }
  try {
    if ((await resetOwnedNativeTarget(deviceId, target)) === 'unsupported') {
      orphan('the camera cannot reset it');
    }
  } catch (error) {
    orphan(error instanceof Error ? error.message : String(error));
  }
};

/** Whether a remaining preset points at the same camera preset. */
const stillMapped = (proxy: Proxy, deviceId: Device['id'], target: NativeTarget): boolean =>
  Object.values(
    proxy.state.control?.studio?.device?.[deviceId]?.control?.presets?.items ?? {}
  ).some((item) => {
    const other = pbNativeTargetToJs(item?.nativeTarget);
    if (!other || other.kind !== target.kind) return false;
    if (other.kind === 'visca_slot' && target.kind === 'visca_slot') {
      return other.slot === target.slot;
    }
    return (
      other.kind === 'onvif_token' &&
      target.kind === 'onvif_token' &&
      other.token === target.token &&
      (other.profile ?? '') === (target.profile ?? '')
    );
  });
